import { X } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { Icon } from '../icons/Icon'
import { usePanelHit } from '../responsive/hitTarget'
import './field.css'
import './tagEditor.css'


/** "  #Costco-run " → "Costco-run". Empty means nothing to add. */
export function cleanTag(entry: string): string {
  return entry.trim().replace(/^#+/, '').trim()
}

type Props = {
  tags: readonly string[]
  /** Known tag values, offered while typing. */
  suggestions: readonly string[]
  disabled: boolean
  /** The id of the text that says why `disabled`, when it is. */
  reasonId: string | undefined
  /** The WHOLE new list. Tags replace, never append (the server takes the list). */
  onChange: (next: string[]) => void
  /** A cap on the list, and the sentence that says so. Omit it for a list with none. */
  ceiling?: { max: number; note: string }
  /** The id of the visible label, so the chips and their controls read as one named group. */
  labelledBy?: string
}

/**
 * Tag chips with remove buttons and an add affordance. **Opaque surfaces only:**
 * the chip is a translucent `--accent-soft` tint with `--accent-ink` text, which
 * fails contrast over glass or the page wash.
 */
export function TagEditor({ tags, suggestions, disabled, reasonId, onChange, ceiling, labelledBy }: Props) {
  const [adding, setAdding] = useState(false)
  const [text, setText] = useState('')
  const addButton = useRef<HTMLButtonElement>(null)
  const listId = useId()
  const hit = usePanelHit()
  const ceilingId = useId()
  const full = ceiling !== undefined && tags.length >= ceiling.max

  const close = () => {
    setAdding(false)
    setText('')
    // The input is about to unmount; focus lands on the control that opened it.
    queueMicrotask(() => addButton.current?.focus())
  }
  const commit = () => {
    const tag = cleanTag(text)
    if (tag !== '' && !tags.includes(tag) && !full) onChange([...tags, tag])
    close()
  }

  return (
    <div className="txn-tags" role="group" aria-labelledby={labelledBy}>
      {tags.map((t) => (
        <span key={t} className="txn-tag">
          {`#${t}`}
          <button
            type="button"
            className={`txn-tag__remove ${hit.className}`}
            aria-label={`Remove tag ${t}`}
            disabled={disabled}
            aria-describedby={reasonId}
            style={{ minHeight: hit.minHeight, minWidth: hit.minHeight }}
            onClick={() => {
              onChange(tags.filter((x) => x !== t))
              addButton.current?.focus()
            }}
          >
            <Icon glyph={X} size={12} />
          </button>
        </span>
      ))}
      {adding ? (
        <>
          <input
            className={`field__input txn-tags__input ${hit.className}`}
            style={{ minHeight: hit.minHeight }}
            type="text"
            aria-label="New tag"
            list={listId}
            value={text}
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- focus follows the click that opened it
            autoFocus
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commit()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                close()
              }
            }}
          />
          <datalist id={listId}>
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </>
      ) : (
        <>
          <button
            ref={addButton}
            type="button"
            className={`txn-tags__add ${hit.className}`}
            style={{ minHeight: hit.minHeight }}
            disabled={disabled}
            aria-disabled={full || undefined}
            aria-describedby={disabled ? reasonId : full ? ceilingId : undefined}
            onClick={() => {
              if (!full) setAdding(true)
            }}
          >
            + Add tag
          </button>
          {full && (
            <span id={ceilingId} role="note" className="note">
              {ceiling.note}
            </span>
          )}
        </>
      )}
    </div>
  )
}
